/*----------------------------------
- MARKETING PAGE: HERO SAMPLE CARD
----------------------------------*/

/**
 * The still sample card beside the hero copy of the product feature pages and the solutions pages: a catalog
 * `HeroSample` drawn by the marketing `MarketingHeroMock` part (`mk-hm`, `aria-hidden` and `inert` in the part), each
 * row's name in the kit `Name`, its note in a kit status `Chip` and its figure in the kit `Price`. It replaced the
 * website's `HeroMockCard` and its tone dots when both families moved onto the design system (batch 7).
 *
 * @docs docs/features/website-product-feature-pages
 * @rule Sample data only, never a real domain row (see `catalogs/landing/heroSample.ts`).
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import { Chip } from '@uniquedomains/components/kit/chip/Chip';
import { Name } from '@uniquedomains/components/kit/name/Name';
import { Price } from '@uniquedomains/components/kit/price/Price';
import { MarketingHeroMock, type HeroMockRow } from '@uniquedomains/components/marketing/marketing-page/MarketingHeroMock';

import type { HeroSample, HeroSampleRow } from '@/client/catalogs/landing/heroSample';
import Icon from '@/client/components/ui/icon/Icon';

/*----------------------------------
- TYPES
----------------------------------*/

type HeroSampleCardProps = {
    sample: HeroSample;
};

/*----------------------------------
- HELPERS
----------------------------------*/

/** A rejected row carries no price: the dash is an empty value, not prose. */
const EMPTY_VALUE = '\u2014';

const toPartRow = ({ lead, note, tone, value }: HeroSampleRow): HeroMockRow => ({
    key: lead.label ?? `${lead.word}.${lead.tld}`,
    lead: lead.label !== undefined ? <span class="ud-cell__lead">{lead.label}</span> : <Name word={lead.word} tld={lead.tld} />,
    status: <Chip tone={tone} label={note} />,
    value: value == null ? <span class="mk-none">{EMPTY_VALUE}</span> : <Price value={value} />,
});

/*----------------------------------
- COMPONENT
----------------------------------*/

const HeroSampleCard = ({ sample }: HeroSampleCardProps) => (
    <MarketingHeroMock
        tile={sample.iconName ? <Icon name={sample.iconName} size={15} strokeWidth={2} /> : sample.letter}
        title={sample.title}
        meta={sample.subtitle}
        badge={sample.badge}
        chips={sample.chips}
        rows={sample.rows.map(toPartRow)}
    />
);

export default HeroSampleCard;
